import { useRouter } from "expo-router";
import { User, X } from "lucide-react-native";
import { ComponentRef, FC, useEffect, useRef, useState } from "react";
import { Modal, Text as RNText } from "react-native";

import {
  Box,
  Card,
  Center,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization, useShareToken, useStudyContext } from "@/hooks";
import { Answer as AnswerType } from "@/types/resources";

/* ── Types ─────────────────────────────────────────────── */

interface AnswerProps {
  answer: AnswerType;
  questionId?: string;
  runId?: string | null;
  showOriginal: boolean;
}

/* ── Functions ──────────────────────────────────────────── */

const PURPLE = "#7c3aed";
const PURPLE_BG = "#f5f3ff";

/* ── Component ──────────────────────────────────────────── */

const Answer: FC<AnswerProps> = ({
  answer,
  questionId,
  runId,
  showOriginal,
}) => {
  const { i18n } = useLocalization();
  const router = useRouter();
  const shareToken = useShareToken();
  const isReadOnly = shareToken !== null;
  const { studyId, studyType } = useStudyContext();
  const { persona } = answer;
  const [isHovered, setIsHovered] = useState(false);
  const isClickable = !isReadOnly && studyType !== "quantitative" && !!runId;

  const [modalVisible, setModalVisible] = useState(false);

  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<ComponentRef<typeof RNText>>(null);

  const hasTranslation = !!answer.answer.translated_text;
  const isShowingTranslation = !showOriginal && hasTranslation;
  const displayText = showOriginal
    ? (answer.answer.text ?? answer.answer.translated_text)
    : (answer.answer.translated_text ?? answer.answer.text);

  useEffect(() => {
    const el = textRef.current as unknown as HTMLElement | null;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [displayText]);

  const locationParts = [
    persona.residency.city,
    persona.residency.state ?? persona.residency.region,
    persona.residency.country,
  ].filter(Boolean);

  const handlePress = () => {
    if (!runId) return;
    if (studyId) {
      router.push({
        pathname: `/studies/${studyId}/personas/${persona.id}`,
        params: { runId },
      });
    } else if (questionId) {
      router.push({
        pathname: `/adhoc/questions/${questionId}/personas/${persona.id}`,
        params: { runId },
      });
    }
  };

  return (
    <>
      <Box
        className="h-full"
        onMouseEnter={isClickable ? () => setIsHovered(true) : undefined}
        onMouseLeave={isClickable ? () => setIsHovered(false) : undefined}
      >
        <Pressable
          className={`h-full data-[disabled=true]:opacity-100 ${!isClickable ? "cursor-default" : ""}`}
          disabled={!isClickable}
          onPress={handlePress}
        >
          <Card
            className={`p-5 rounded-2xl border border-outline-200 bg-background-0 h-full transition-all duration-300 ${isClickable ? "hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5" : ""}`}
          >
            <VStack className="h-full justify-between gap-4">
              {/* Identity */}
              <HStack className="items-center gap-3">
                <Box
                  className="w-10 h-10 rounded-full items-center justify-center shrink-0"
                  style={{ backgroundColor: PURPLE_BG }}
                >
                  <User color={PURPLE} size={18} />
                </Box>
                <VStack className="gap-0.5 min-w-0 flex-1">
                  <Text
                    className="text-sm font-semibold text-typography-900 dark:text-typography-100 truncate"
                    style={
                      isHovered && isClickable ? { color: PURPLE } : undefined
                    }
                  >
                    {i18n.t("dashboardNavigator.age", {
                      age: persona.general.age,
                    })}
                    {persona.general.gender
                      ? ` · ${persona.general.gender}`
                      : ""}
                  </Text>
                  <Text className="text-xs text-typography-400 truncate">
                    {locationParts.join(" · ")}
                  </Text>
                </VStack>
              </HStack>

              {/* Quote + translation note pinned to bottom */}
              <VStack className="gap-1.5">
                <Text
                  ref={textRef}
                  className="text-sm text-typography-500 leading-relaxed italic overflow-hidden"
                  // @ts-expect-error - web-only CSS for line clamping
                  style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 8,
                  }}
                >
                  {displayText}
                </Text>
                {isTruncated && (
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      setModalVisible(true);
                    }}
                  >
                    <Text
                      className="text-xs font-medium"
                      style={{ color: PURPLE }}
                    >
                      {i18n.t("misc.readMore")}
                    </Text>
                  </Pressable>
                )}
                {isShowingTranslation && (
                  <Text
                    className="text-[11px]"
                    style={{ color: `${PURPLE}99` }}
                  >
                    {i18n.t("misc.translatedFrom", {
                      language: persona.general.language,
                    })}
                  </Text>
                )}
              </VStack>
            </VStack>
          </Card>
        </Pressable>
      </Box>

      {/* Full verbatim modal */}
      <Modal
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
        transparent
        visible={modalVisible}
      >
        <Center className="flex-1 bg-black/60">
          <Pressable
            className="absolute inset-0"
            onPress={() => setModalVisible(false)}
          />
          <Box
            className="bg-white dark:bg-background-900 rounded-2xl p-6 overflow-auto"
            style={{ maxWidth: 560, width: "90%", maxHeight: "80%" }}
          >
            <VStack className="gap-4">
              <HStack className="items-center justify-between">
                <HStack className="items-center gap-3">
                  <Box
                    className="w-10 h-10 rounded-full items-center justify-center shrink-0"
                    style={{ backgroundColor: PURPLE_BG }}
                  >
                    <User color={PURPLE} size={18} />
                  </Box>
                  <VStack className="gap-0.5">
                    <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
                      {i18n.t("dashboardNavigator.age", {
                        age: persona.general.age,
                      })}
                      {persona.general.gender
                        ? ` · ${persona.general.gender}`
                        : ""}
                    </Text>
                    <Text className="text-xs text-typography-400">
                      {locationParts.join(" · ")}
                    </Text>
                  </VStack>
                </HStack>
                <Pressable
                  className="w-8 h-8 rounded-full items-center justify-center"
                  onPress={() => setModalVisible(false)}
                  style={{ backgroundColor: "#f3f4f6" }}
                >
                  <X color="#6b7280" size={16} />
                </Pressable>
              </HStack>

              <Text className="text-sm text-typography-600 leading-relaxed italic">
                {displayText}
              </Text>

              {isShowingTranslation && (
                <Text className="text-[11px]" style={{ color: `${PURPLE}99` }}>
                  {i18n.t("misc.translatedFrom", {
                    language: persona.general.language,
                  })}
                </Text>
              )}
            </VStack>
          </Box>
        </Center>
      </Modal>
    </>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default Answer;
